import type { MidiNote, PitchFrame } from "./music";

export type VoiceMapSample = {
  targetMidi: number;
  observedMidi: number | null;
  pitchOffsetCents: number | null;
  medianLevel: number;
  voicedRatio: number;
  sustainRatio: number;
  confidence: number;
  weak: boolean;
};

export type VoiceMapProfile = {
  schemaVersion: 1;
  createdAt: string;
  deviceLabel: string;
  preserveSoftTone: boolean;
  noiseP90: number;
  centerMidi: number;
  samples: VoiceMapSample[];
  sustainCaptureRatio: number;
  recommendedSensitivity: number;
};
export type VoiceMapImpact = {
  captureThreshold: number;
  captureReductionPercent: number;
  weakestConfidenceThreshold: number;
  confidenceReductionPercent: number;
  sustainExtensionPercent: number;
};


function median(values: number[]): number {
  if (!values.length) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[middle] : (sorted[middle - 1] + sorted[middle]) / 2;
}

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.max(minimum, Math.min(maximum, value));
}

export function analyzeVoiceMapSample(targetMidi: number, frames: PitchFrame[], durationSeconds: number): VoiceMapSample {
  const matching = frames.filter((frame) => (
    frame.midiFloat !== null
    && Math.abs(frame.midiFloat - targetMidi) <= 3
    && frame.confidence >= 0.38
  ));
  const observedMidi = matching.length ? median(matching.map((frame) => frame.midiFloat as number)) : null;
  const levels = matching.map((frame) => frame.level ?? 0).filter((level) => level > 0);
  const medianLevel = median(levels);
  const voicedRatio = frames.length ? matching.length / frames.length : 0;
  const firstTime = matching.at(0)?.time ?? 0;
  const lastTime = matching.at(-1)?.time ?? firstTime;
  const sustainRatio = clamp((lastTime - firstTime + 0.055) / Math.max(0.055, durationSeconds), 0, 1);
  const confidence = matching.length ? median(matching.map((frame) => frame.confidence)) : 0;
  return {
    targetMidi,
    observedMidi,
    pitchOffsetCents: observedMidi === null ? null : Math.round((observedMidi - targetMidi) * 100),
    medianLevel,
    voicedRatio,
    sustainRatio,
    confidence,
    weak: voicedRatio < 0.45 || medianLevel < 0.01,
  };
}

export function buildVoiceMapProfile(
  samples: VoiceMapSample[],
  longSample: VoiceMapSample,
  metadata: Pick<VoiceMapProfile, "deviceLabel" | "preserveSoftTone" | "noiseP90" | "centerMidi">,
): VoiceMapProfile {
  const weakCount = samples.filter((sample) => sample.weak).length;
  const sustainCaptureRatio = clamp(longSample.sustainRatio || longSample.voicedRatio, 0.45, 1);
  return {
    schemaVersion: 1,
    createdAt: new Date().toISOString(),
    ...metadata,
    samples,
    sustainCaptureRatio,
    recommendedSensitivity: Math.round(clamp(52 + weakCount * 5 + (1 - sustainCaptureRatio) * 28, 45, 90)),
  };
}

function nearestSample(profile: VoiceMapProfile, midi: number): VoiceMapSample | null {
  return profile.samples.reduce<VoiceMapSample | null>((nearest, sample) => {
    if (!nearest) return sample;
    return Math.abs(sample.targetMidi - midi) < Math.abs(nearest.targetMidi - midi) ? sample : nearest;
  }, null);
}

function thresholdFactor(profile: VoiceMapProfile, midi: number): number {
  const usableLevels = profile.samples.map((sample) => sample.medianLevel).filter((level) => level > 0);
  const typicalLevel = median(usableLevels) || 0.012;
  const sample = nearestSample(profile, midi);
  if (!sample) return 1;
  const relativeLevel = clamp(sample.medianLevel / typicalLevel, 0.2, 1);
  const captured = clamp(sample.voicedRatio, 0.2, 1);
  return clamp(0.5 + 0.5 * Math.sqrt(relativeLevel * captured), 0.52, 1);
}

export function voiceCaptureThreshold(baseThreshold: number, profile: VoiceMapProfile): number {
  const weakestFactor = profile.samples.length
    ? Math.min(...profile.samples.map((sample) => thresholdFactor(profile, sample.targetMidi)))
    : 1;
  return Math.max(0.0035, baseThreshold * weakestFactor);
}
export function voiceMapImpact(
  profile: VoiceMapProfile,
  baseRmsThreshold: number,
  baseConfidenceThreshold: number,
): VoiceMapImpact {
  const captureThreshold = voiceCaptureThreshold(baseRmsThreshold, profile);
  const factors = profile.samples.map((sample) => thresholdFactor(profile, sample.targetMidi));
  const weakestFactor = factors.length ? Math.min(...factors) : 1;
  const weakestConfidenceThreshold = Math.max(0.38, baseConfidenceThreshold * weakestFactor);
  const sustainMultiplier = clamp(1 / profile.sustainCaptureRatio, 1, 1.5);
  return {
    captureThreshold,
    captureReductionPercent: Math.round((1 - captureThreshold / Math.max(0.0001, baseRmsThreshold)) * 100),
    weakestConfidenceThreshold,
    confidenceReductionPercent: Math.round((1 - weakestConfidenceThreshold / Math.max(0.0001, baseConfidenceThreshold)) * 100),
    sustainExtensionPercent: Math.round((sustainMultiplier - 1) * 100),
  };
}

export function recommendedSensitivityFromLiveLevel(signalLevel: number, captureFactor = 1): number {
  if (!Number.isFinite(signalLevel) || signalLevel <= 0) return 100;
  const safeFactor = clamp(captureFactor, 0.35, 1);
  const targetEffectiveThreshold = Math.max(0.0035, signalLevel * 0.72);
  const targetBaseThreshold = targetEffectiveThreshold / safeFactor;
  return Math.round(clamp(Math.ceil((0.026 - targetBaseThreshold) / 0.00022), 0, 100));
}


export function applyVoiceMapToFrame(frame: PitchFrame, profile: VoiceMapProfile, baseConfidenceThreshold: number): PitchFrame {
  if (frame.midiFloat === null) return frame;
  const factor = thresholdFactor(profile, frame.midiFloat);
  return {
    ...frame,
    acceptanceConfidence: Math.max(0.38, baseConfidenceThreshold * factor),
  };
}

export function applyVoiceSustainAssist(notes: MidiNote[], profile: VoiceMapProfile, gridBeats: number): MidiNote[] {
  const multiplier = clamp(1 / profile.sustainCaptureRatio, 1, 1.5);
  if (multiplier < 1.04) return notes.map((note) => ({ ...note }));
  return notes.map((note, index) => {
    if (note.durationBeats < gridBeats * 2 - 0.0001) return { ...note };
    const nextStart = notes.slice(index + 1).map((next) => next.startBeat).find((start) => start > note.startBeat);
    const desired = Math.min(note.durationBeats * multiplier, note.durationBeats + gridBeats * 2);
    const available = nextStart === undefined ? desired : Math.min(desired, nextStart - note.startBeat);
    return { ...note, durationBeats: Math.max(note.durationBeats, available) };
  });
}

export function voiceMapMatches(profile: VoiceMapProfile, deviceLabel: string, preserveSoftTone: boolean): boolean {
  return profile.deviceLabel === deviceLabel && profile.preserveSoftTone === preserveSoftTone;
}
